/**
 * @file     listingKinds.ts
 * @purpose  The listing-kind registry for owner listings. A kind supplies its
 *           field schema, photo rules, step weights, public route, end
 *           states, expiry and enquiry lead type; every screen, the state
 *           machine, media, verification and review are shared.
 *           Adding a kind (sale, coming soon, assignment, partner licensee) is a
 *           new entry here plus a row in listing_kinds. Only 'lease' ships.
 */
import '@/core/i18n/deferred/listings';
import { z } from 'zod';
import type { ListingEndReason } from './listingLifecycle';

export const LISTING_STEP_IDS = ['address', 'details', 'photos', 'description', 'pricing', 'review'] as const;
export type ListingStepId = (typeof LISTING_STEP_IDS)[number];

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

/** Shared by every kind. The street number stays private until a viewing is set. */
export const listingAddressSchema = z.object({
  streetNumber: z.string().trim().min(1).max(12),
  streetName: z.string().trim().min(2).max(80),
  unit: z.string().trim().max(12).optional(),
  city: z.string().trim().min(2).max(60),
  province: z.literal('BC'),
  postalCode: z.string().trim().regex(/^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/).optional(),
});
export type ListingAddress = z.infer<typeof listingAddressSchema>;

const CONTACT_PATTERN = /(\b\d{3}[\s.-]?\d{3}[\s.-]?\d{4}\b)|([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/i;
/** True when owner text carries a phone number or an email. Enquiries go to the licensee. */
export const hasContactDetails = (v: string) => CONTACT_PATTERN.test(v);
const noContactDetails = (v: string) => !hasContactDetails(v);

/** Shared by every kind. Contact details are refused: enquiries go to the licensee. */
export const listingContentSchema = z.object({
  headline: z.string().trim().min(8).max(80).refine(noContactDetails, 'contact_details'),
  description: z.string().trim().min(120).max(2000).refine(noContactDetails, 'contact_details'),
});
export type ListingContent = z.infer<typeof listingContentSchema>;

/**
 * Drafts save while the owner types, so length floors wait for submit; the
 * ceilings and the contact-details refusal hold on every save.
 */
export const listingContentDraftSchema = z.object({
  headline: z.string().max(80).refine(noContactDetails, 'contact_details'),
  description: z.string().max(2000).refine(noContactDetails, 'contact_details'),
});

export const LEASE_HOME_TYPES = ['apartment_condo', 'townhouse', 'house', 'suite', 'laneway', 'duplex', 'other'] as const;
export const LEASE_TERMS = ['month_to_month', 'six_months', 'one_year', 'other'] as const;
export const LEASE_FURNISHED = ['furnished', 'partly', 'unfurnished'] as const;
export const LEASE_PETS = ['yes', 'cats', 'dogs', 'no', 'ask'] as const;
export const LEASE_LAUNDRY = ['in_unit', 'in_building', 'none'] as const;
export const LEASE_UTILITIES = ['heat', 'hot_water', 'electricity', 'water', 'gas', 'internet'] as const;

const leaseAttributesBase = z.object({
  homeType: z.enum(LEASE_HOME_TYPES),
  bedrooms: z.number().int().min(0).max(10),
  bathrooms: z.number().min(0).max(10).multipleOf(0.5),
  livingArea: z.number().int().min(100).max(20000).optional(),
  availableOn: isoDate,
  leaseTerm: z.enum(LEASE_TERMS),
  furnished: z.enum(LEASE_FURNISHED),
  pets: z.enum(LEASE_PETS),
  laundry: z.enum(LEASE_LAUNDRY),
  parking: z.number().int().min(0).max(6),
  utilitiesIncluded: z.array(z.enum(LEASE_UTILITIES)).max(LEASE_UTILITIES.length),
  rules: z.string().trim().max(500).refine(noContactDetails, 'contact_details').optional(),
  securityDeposit: z.number().min(0).optional(),
  petDeposit: z.number().min(0).optional(),
});

export type LeaseAttributes = z.infer<typeof leaseAttributesBase>;

/** Residential Tenancy Act: security and pet deposits are each capped at half a month's rent. */
export function depositCap(monthlyRent: number): number {
  return Math.floor((monthlyRent / 2) * 100) / 100;
}

/**
 * How the builder's Details step renders one attribute. Labels, options and
 * the "why" line come from i18n: listings.fields.{key}.label|why|options.{value}.
 */
export type ListingFieldSpec = {
  key: string;
  control: 'choice' | 'multi' | 'stepper' | 'number' | 'date' | 'text';
  options?: readonly string[];
  required: boolean;
  min?: number;
  max?: number;
  step?: number;
};

export type ListingKindDefinition = {
  key: string;
  labelKey: string;
  pricePeriod: 'month' | null;
  priceRange: { min: number; max: number };
  /** Full schema checked on submit; drafts are checked against its partial. */
  attributesSchema: z.ZodObject<z.ZodRawShape>;
  /** Field order and controls for the builder's Details step. */
  fields: readonly ListingFieldSpec[];
  /** Cross-field rules that need the price (e.g. deposit caps). Returns error codes. */
  validateWithPrice: (attributes: Record<string, unknown>, price: number | null) => string[];
  photos: { min: number; max: number };
  stepWeights: Record<ListingStepId, number>;
  publicPath: (slug: string) => string;
  jsonLdType: 'LeaseOut' | 'Offer';
  endReasons: readonly ListingEndReason[];
  expiryDays: number;
  enquiryLeadType: string;
};

export const LISTING_KINDS = {
  lease: {
    key: 'lease',
    labelKey: 'listings.kinds.lease.label',
    pricePeriod: 'month',
    priceRange: { min: 300, max: 50000 },
    attributesSchema: leaseAttributesBase,
    fields: [
      { key: 'homeType', control: 'choice', options: LEASE_HOME_TYPES, required: true },
      { key: 'bedrooms', control: 'stepper', required: true, min: 0, max: 10, step: 1 },
      { key: 'bathrooms', control: 'stepper', required: true, min: 0, max: 10, step: 0.5 },
      { key: 'livingArea', control: 'number', required: false, min: 100, max: 20000 },
      { key: 'availableOn', control: 'date', required: true },
      { key: 'leaseTerm', control: 'choice', options: LEASE_TERMS, required: true },
      { key: 'furnished', control: 'choice', options: LEASE_FURNISHED, required: true },
      { key: 'pets', control: 'choice', options: LEASE_PETS, required: true },
      { key: 'laundry', control: 'choice', options: LEASE_LAUNDRY, required: true },
      { key: 'parking', control: 'stepper', required: true, min: 0, max: 6, step: 1 },
      { key: 'utilitiesIncluded', control: 'multi', options: LEASE_UTILITIES, required: true },
      { key: 'rules', control: 'text', required: false, max: 500 },
    ],
    validateWithPrice: (attributes, price) => {
      if (price == null) return [];
      const cap = depositCap(price);
      const errors: string[] = [];
      if (typeof attributes.securityDeposit === 'number' && attributes.securityDeposit > cap) errors.push('security_deposit_over_cap');
      if (typeof attributes.petDeposit === 'number' && attributes.petDeposit > cap) errors.push('pet_deposit_over_cap');
      return errors;
    },
    photos: { min: 6, max: 30 },
    stepWeights: { address: 15, details: 25, photos: 25, description: 15, pricing: 15, review: 5 },
    publicPath: (slug) => `/rentals/listed/${slug}`,
    jsonLdType: 'LeaseOut',
    endReasons: ['leased', 'other'],
    expiryDays: 90,
    enquiryLeadType: 'owner_listing',
  },
} as const satisfies Record<string, ListingKindDefinition>;

export type ListingKindKey = keyof typeof LISTING_KINDS;

export function isListingKind(value: unknown): value is ListingKindKey {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(LISTING_KINDS, value);
}

export function getListingKind(key: ListingKindKey): ListingKindDefinition {
  return LISTING_KINDS[key];
}
